/**
 * The gear icon in the staff header: theme toggle, the caller's own display
 * name, and — for whoever holds `settings:write` — a link out to
 * `/staff/company-information`. That link used to be an inline form here
 * (`CompanySettingsPage.tsx` had been folded in for the same reason a page
 * was overkill for one field), but the company profile now covers 8+
 * fields feeding document autofill — enough that a dedicated page is the
 * better fit again. See CompanyInformationPage.tsx.
 *
 * A self-contained panel with its own busy/error/saved state, same shape as
 * SensitivePanel, rather than a Refine `useForm` screen — this isn't a
 * resource with a list or a show page, just the signed-in actor's own row.
 */

import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Landmark, Percent, ScrollText } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../lib/api.js';
import { useActor, useCan } from '../lib/capabilities.js';
import { useSession } from '../lib/session.js';
import { useTheme } from '../lib/theme.js';
import { ChangePasswordForm } from './ChangePasswordForm.js';
import { Field, FormErrors, TextInput } from './Form.js';

function GearIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

// Stripe's actual current icon mark (their "S" glyph — the same shape used
// in their app icon/favicon), on their brand-purple rounded square, matching
// how the mark is officially presented standalone rather than as the full
// wordmark, which wouldn't read at this button's size. Path data is Stripe's
// own vector, not a hand-drawn approximation — sourced from Simple Icons
// (simpleicons.org), the standard reference for exact brand-mark SVGs.
function StripeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect width="16" height="16" rx="3" fill="#635bff" />
      <svg x="3" y="3" width="10" height="10" viewBox="0 0 24 24">
        <path
          fill="#ffffff"
          d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"
        />
      </svg>
    </svg>
  );
}

export function SettingsPanel() {
  const actor = useActor();
  const { refresh } = useSession();
  const { theme, setTheme, error: themeError } = useTheme();
  const canWriteCompanySettings = useCan('settings:write');
  const canWriteCompliancePages = useCan('compliance-pages:write');

  const [open, setOpen] = useState(false);
  const [name, setName] = useState(actor?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setName(actor?.name ?? '');
  }, [actor?.name]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // StaffLayout is already staff-only, so this never renders for a portal
  // actor — but the settings concept genuinely doesn't apply before the
  // session resolves.
  if (!actor) return null;

  const isAdmin = actor.staffRole === 'ADMIN';

  async function saveName(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNameError(null);
    setSaved(false);
    try {
      await api('/me', { method: 'PATCH', body: JSON.stringify({ name }) });
      await refresh();
      setSaved(true);
    } catch (caught) {
      setNameError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings" ref={panelRef}>
      {/* Stacked, not side by side — same 2rem square as the gear trigger,
          just a second one underneath it. Gated on settings:write (ADMIN
          only): same "one account's call" reasoning as the Company
          Information/Fee Schedule links below, not a new capability for what
          is really just a bookmark — Stripe's own login still gates the
          dashboard itself, this only saves someone who already has Stripe
          access a trip to type the URL. */}
      <div className="settings__triggers">
        <button
          type="button"
          className="settings__trigger"
          aria-label="Settings"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <GearIcon />
        </button>
        {canWriteCompanySettings ? (
          <a
            href="https://dashboard.stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="settings__trigger"
            aria-label="Open Stripe Dashboard (opens in a new tab)"
            title="Open Stripe Dashboard"
          >
            <StripeIcon />
          </a>
        ) : null}
      </div>

      {open ? (
        <div className="settings__panel card stack">
          <div className="settings__row">
            <span className="field__label">Theme</span>
            <div className="row">
              <button
                type="button"
                className={theme === 'LIGHT' ? 'button' : 'button button--quiet'}
                onClick={() => setTheme('LIGHT')}
              >
                Light
              </button>
              <button
                type="button"
                className={theme === 'DARK' ? 'button' : 'button button--quiet'}
                onClick={() => setTheme('DARK')}
              >
                Dark
              </button>
            </div>
          </div>
          {isAdmin ? <p className="muted">Saved in this browser only.</p> : null}
          {themeError ? <FormErrors error={themeError} /> : null}

          <form className="stack" onSubmit={(event) => void saveName(event)}>
            <Field
              label="Display name"
              name="name"
              {...(isAdmin ? { hint: 'Contact tech support to change more details.' } : {})}
            >
              <TextInput name="name" value={name} onChange={setName} required disabled={isAdmin} />
            </Field>
            {!isAdmin ? (
              <button type="submit" className="button" disabled={busy || name.trim() === ''}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            ) : null}
            {nameError ? <FormErrors error={nameError} /> : null}
            {saved ? <p className="muted">Saved.</p> : null}
          </form>

          <div className="stack">
            <span className="field__label">Password</span>
            {/* Unlike Display name above, this is NOT disabled for ADMIN.
                Better Auth's change-password endpoint runs on
                systemPrisma/app_migrator (BYPASSRLS), never through
                app_user/RLS — it was never gated by the staff-only account
                policies that make the admin row immutable through the app
                for everything else. It also requires the caller's current
                password, so a stolen session cookie alone can't use it.
                Letting ADMIN rotate their own password here is deliberate —
                see CLAUDE.md's admin-immutability note. */}
            <ChangePasswordForm />
          </div>

          <div className="settings__row">
            <span className="field__label">Email</span>
            <span className="settings__static muted">{actor.email}</span>
          </div>

          {canWriteCompanySettings ? (
            <Link
              to="/staff/company-information"
              className="settings__link"
              onClick={() => setOpen(false)}
            >
              <Landmark size={14} strokeWidth={2} aria-hidden="true" />
              Company Information
            </Link>
          ) : null}

          {canWriteCompanySettings ? (
            <Link
              to="/staff/fee-schedule"
              className="settings__link"
              onClick={() => setOpen(false)}
            >
              <Percent size={14} strokeWidth={2} aria-hidden="true" />
              Fee Schedule
            </Link>
          ) : null}

          {canWriteCompliancePages ? (
            <Link
              to="/staff/compliance-pages"
              className="settings__link"
              onClick={() => setOpen(false)}
            >
              <ScrollText size={14} strokeWidth={2} aria-hidden="true" />
              Compliance Pages
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
